import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  Select,
} from '@/components/ui';
import { auditActionTone } from '@/features/admin/status';
import { errorMessage } from '@/hooks/useApiError';
import { relativeTime } from '@/utils/format';

const RESOURCE_TYPES = ['user', 'company', 'company_member', 'internship', 'application', 'resume'];

const PAGE_SIZE = 20;

export function AdminAuditPage() {
  const [action, setAction] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(action.trim() ? { action: action.trim() } : {}),
    ...(resourceType ? { resource_type: resourceType } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-audit', params],
    queryFn: () => adminApi.listAuditLog(params),
    // Typing in the filter must not unmount the page it is typed into: with
    // no placeholder, every keystroke swapped the whole screen for a spinner
    // and the field lost focus mid-word.
    placeholderData: keepPreviousData,
  });

  const activeCount = [action !== '', resourceType !== ''].filter(Boolean).length;

  function resetFilters() {
    setAction('');
    setResourceType('');
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Audit log"
        description="Every security- and business-sensitive action, newest first."
      />

      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarEnd={
          <>
            <Input
              label="Action"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-56"
              placeholder="Action, exact match"
              value={action}
              onChange={(event) => {
                setAction(event.target.value);
                setPage(1);
              }}
            />
            <Select
              label="Resource"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={resourceType}
              onChange={(event) => {
                setResourceType(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All resources</option>
              {RESOURCE_TYPES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading the audit log…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="Nothing logged"
                description="Nothing matches these filters."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="Nothing logged yet"
                description="Security- and business-sensitive actions are recorded here as they happen."
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <ol className="divide-line divide-y">
          {rows.map((row) => (
            <li key={row.id} className="hover:bg-brand-50/40 px-5 py-4 transition">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={auditActionTone(row.action)}>{row.action}</Badge>
                {row.actor_role && <span className="text-ink-muted text-xs">{row.actor_role}</span>}
                <span className="text-ink-muted ml-auto text-xs">
                  {relativeTime(row.created_at)}
                </span>
              </div>
              <p className="text-ink-muted mt-1 text-xs">
                {row.resource_type && (
                  <>
                    {row.resource_type} {row.resource_id?.slice(0, 8)}
                  </>
                )}
                {row.ip_address && ` · ${row.ip_address}`}
              </p>
              {(row.before || row.after) && (
                <pre className="bg-surface-muted text-ink-muted mt-2 overflow-x-auto rounded p-2 text-xs">
                  {JSON.stringify({ before: row.before, after: row.after }, null, 2)}
                </pre>
              )}
            </li>
          ))}
        </ol>
      </ListCard>
    </div>
  );
}
